import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, ChevronUp, LogOut, Layers, Settings, Star,
} from 'lucide-react';
import selectStyles from './CustomSelect.module.css';
import { useProfile } from '../../Providers/ProfileProvider';
import { logout } from '../../apiServices/auth/email';

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-sm)',
  height: '34px',
  padding: '0 var(--space-sm)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-secondary)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

export default function ProfileWorkspaceSelect() {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentWorkspace = profile?.config?.currentWorkspace;
  const initial = profile?.pseudo ? profile.pseudo[0].toUpperCase() : '';

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('firstTime');
    navigate('/login');
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative', width: '100%', boxSizing: 'border-box', padding: '10px',
      }}
    >
      <button
        type="button"
        className={selectStyles.hoverLign}
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: '100%',
          height: '38px',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          background: 'none',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          boxSizing: 'border-box',
          padding: '0 var(--space-xs)',
        }}
      >
        <div
          style={{
            width: '26px',
            height: '26px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-accent)',
            color: 'white',
            borderRadius: 'var(--radius-circle)',
            fontWeight: 600,
            fontSize: '0.85rem',
          }}
        >
          {initial}
        </div>
        <span style={{
          flex: 1,
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          fontSize: '15px',
          color: 'var(--color-text-primary)',
        }}
        >
          {profile?.pseudo || ''}
        </span>
        {isOpen
          ? <ChevronUp size={16} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
          : <ChevronDown size={16} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />}
      </button>

      {isOpen && (
        <div
          className={`${selectStyles.scrollableContent} ${selectStyles.dropdown}`}
          style={{
            position: 'absolute',
            top: 'calc(100% - 2px)',
            left: '10px',
            right: 'var(--space-xs)',
            zIndex: 20,
            minWidth: '220px',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxSizing: 'border-box',
            padding: 'var(--space-sm)',
            background: 'var(--color-bg)',
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2xs)',
          }}
          >
            <Link
              to="/profile"
              className={selectStyles.hoverLign}
              onClick={() => setIsOpen(false)}
              style={itemStyle}
            >
              <Settings size={16} />
              {t('nav.settings', { defaultValue: 'Settings' })}
            </Link>

            {currentWorkspace && (
              <Link
                to={`/${currentWorkspace}`}
                className={selectStyles.hoverLign}
                onClick={() => setIsOpen(false)}
                style={itemStyle}
              >
                <Star size={16} fill="var(--color-accent)" color="var(--color-accent)" />
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600,
                }}
                >
                  {currentWorkspace}
                </span>
              </Link>
            )}

            <Link
              to="/workspaces"
              className={selectStyles.hoverLign}
              onClick={() => setIsOpen(false)}
              style={itemStyle}
            >
              <Layers size={16} />
              {t('nav.changeWorkspace', { defaultValue: 'Change workspace' })}
            </Link>

            <button
              type="button"
              className={selectStyles.hoverLign}
              onClick={async () => {
                setIsOpen(false);
                await handleLogout();
              }}
              style={{
                ...itemStyle, border: 'none', background: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
              }}
            >
              <LogOut size={16} />
              {t('nav.logout', { defaultValue: 'Logout' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
